import React, { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { firestore } from '../lib/firebaseConfig';
import Modal from './Modal';
import Button from './Button';
import FormInput from './FormInput';
import FormLabel from './FormLabel';
import BlockTitle from './BlockTitle';
import PhoneNumberField from './PhoneNumberField';
import { useToast } from '../context/ToastContext';

const EditClientModal = ({ isOpen, onClose, client, onSave }) => {
  const { showSuccess, showError } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    address: '',
    pesel: '',
    phone: '',
    email: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (client && isOpen) {
      // Get the first buyer's data or fall back to top-level fields
      const buyerData = client.buyers?.[0] || {};
      setFormData({
        name: buyerData.name || client.name || '',
        surname: buyerData.surname || client.surname || '',
        address: buyerData.address || client.address || '',
        pesel: buyerData.pesel || client.pesel || '',
        phone: buyerData.phone || client.phone || '',
        email: buyerData.email || client.email || '',
      });
    }
  }, [client, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const clientRef = doc(firestore, 'customers', client.id);

      // Update both top-level fields and buyers array
      const updatedData = {
        name: formData.name,
        surname: formData.surname,
        phone: formData.phone,
        email: formData.email,
      };

      // Update buyers array if it exists
      if (client.buyers && client.buyers.length > 0) {
        updatedData.buyers = client.buyers.map((buyer, index) => {
          if (index === 0) {
            return {
              ...buyer,
              name: formData.name,
              surname: formData.surname,
              address: formData.address,
              pesel: formData.pesel,
              phone: formData.phone,
              email: formData.email,
            };
          }
          return buyer;
        });
      }

      await updateDoc(clientRef, updatedData);

      showSuccess('Dane klienta zostały zaktualizowane');
      onSave?.();
      onClose();
    } catch (error) {
      console.error('Error updating client:', error);
      showError('Nie udało się zaktualizować danych klienta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="p-8">
        <BlockTitle className="text-[22px]">
          Edytuj dane klienta
        </BlockTitle>

        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-6 mt-6">
          <div>
            <FormLabel htmlFor="name">Imię</FormLabel>
            <FormInput
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Wprowadź imię"
              required
            />
          </div>

          <div>
            <FormLabel htmlFor="surname">Nazwisko</FormLabel>
            <FormInput
              id="surname"
              name="surname"
              value={formData.surname}
              onChange={handleChange}
              placeholder="Wprowadź nazwisko"
              required
            />
          </div>

          <div className="col-span-2">
            <FormLabel htmlFor="address">Adres</FormLabel>
            <FormInput
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Wprowadź adres"
            />
          </div>

          <div>
            <FormLabel htmlFor="pesel">PESEL</FormLabel>
            <FormInput
              id="pesel"
              name="pesel"
              value={formData.pesel}
              onChange={handleChange}
              placeholder="Wprowadź PESEL"
            />
          </div>

          <div>
            <FormLabel htmlFor="phone">Telefon</FormLabel>
            <PhoneNumberField
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Wprowadź numer telefonu"
            />
          </div>

          <div className="col-span-2">
            <FormLabel htmlFor="email">Email</FormLabel>
            <FormInput
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Wprowadź email"
            />
          </div>

          <div className="col-span-2 flex justify-end gap-4 mt-6">
            <Button type="button" variant="bordered" onClick={onClose}>
              Anuluj
            </Button>
            <Button type="submit" variant="default" disabled={loading}>
              {loading ? 'Zapisywanie...' : 'Zapisz'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default EditClientModal;
